import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { StyleSheet, ScrollView, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import SearchHeader from '@/components/SearchHeader';
import ListItemPlaceholder from '@/components/Placeholders/ListItemPlaceholder';
import StatusContainer from '@/components/StatusContainer';
import MetaHead from '@/components/MetaHead';
import Text from '@/components/Typography/Text';
import { useTheme } from '@/context/ThemeContext';

const createStyles = (colors, insets, hasItemsOrLoading) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.solid_color_primary,
  },
  content: {
    flex: 1,
    paddingTop: hasItemsOrLoading ? 5 : 15,
    paddingHorizontal: 15 + Math.max(insets.left, insets.right),
    paddingBottom: insets.bottom,
  },
  sectionTitle: {
    marginTop: 5,
    marginBottom: 15,
  },
  contentContainer: {
    width: '100%',
    paddingBottom: insets.bottom + 15,
  },
});

const SearchPage = ({
  title,
  description,
  searchPlaceholder,
  cancelRoute,
  isSearching,
  searchError,
  searchResults,
  onSearch,
  onClearSearch,
  renderItem,
  showRecent = false,
  recentItems = [],
  isLoadingRecent = false,
  recentTitle = "Recently Used",
  emptyMessage = "Search",
  noResultsTitle = "No Results",
  searchDebounceMs = 500,
  renderEmpty,
  maxLength,
}) => {
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const hasItemsOrLoading = Array.isArray(searchResults) && searchResults.length > 0 || isSearching || isSearchLoading;
  const styles = useMemo(() => createStyles(colors, insets, hasItemsOrLoading), [colors, insets, hasItemsOrLoading]);

  // Reset search on mount
  useEffect(() => {
    onClearSearch();
  }, []);

  // Debounced search handler
  const handleSearch = useCallback((text) => {
    setSearchQuery(text);
    
    // Clear previous timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Reset search when input is empty
    if (!text.trim()) {
      onClearSearch();
      setIsSearchLoading(false);
      return;
    }

    // Set loading state immediately when user starts typing
    setIsSearchLoading(true);

    // Set new timeout
    const timeout = setTimeout(() => {
      if (text.trim()) {
        onSearch(text).finally(() => {
          setIsSearchLoading(false);
        });
      }
    }, searchDebounceMs);

    setSearchTimeout(timeout);
  }, [searchDebounceMs, onSearch, onClearSearch]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  // Add cleanup effect
  useEffect(() => {
    return () => {
      onClearSearch();
      setSearchQuery('');
      setIsSearchLoading(false);
    };
  }, []);

  const renderContent = () => {
    if ((isSearching || isSearchLoading) && searchQuery.trim()) {
      return (
        <View>
          {[...Array(6)].map((_, index) => (
            <ListItemPlaceholder key={index} />
          ))}
        </View>
      );
    }

    if (searchError) {
      return (
        <StatusContainer
          type="error"
          title="Search Error"
          message={searchError}
          actionText="Try Again"
          onAction={() => onSearch(searchQuery)}
        />
      );
    }

    if (searchQuery.trim() && !isSearching && !isSearchLoading && searchResults.length === 0) {
      return (
        <StatusContainer
          type="empty"
          title={noResultsTitle}
          message={`No results found matching "${searchQuery}"`}
        />
      );
    }

    if (searchResults.length > 0) {
      return searchResults.map(renderItem);
    }

    if (showRecent) {
      if (isLoadingRecent) {
        return (
          <View>
            <Text size='xl' weight='bold' color='primary' style={styles.sectionTitle}>{recentTitle}</Text>
            {[...Array(4)].map((_, index) => (
              <ListItemPlaceholder key={index} />
            ))}
          </View>
        );
      }

      if (recentItems.length === 0) {
        if (typeof renderEmpty === 'function') {
          return renderEmpty();
        }
        return (
          <>
            <Text size='xl' weight='bold' color='primary' style={styles.sectionTitle}>{recentTitle}</Text>
            <StatusContainer
              type="empty"
              message={`No ${recentTitle.toLowerCase()}`}
            />
          </>
        );
      }

      return (
        <>
          <Text size='xl' weight='bold' color='primary' style={styles.sectionTitle}>{recentTitle}</Text>
          {recentItems.map(renderItem)}
        </>
      );
    }

    if (typeof renderEmpty === 'function') {
      return renderEmpty();
    }
    return (
      <StatusContainer
        type="empty"
        message={emptyMessage}
      />
    );
  };
  
  return (
    <View style={styles.container}>
      <MetaHead 
        title={title}
        description={description}
        noindex
      />
      <SearchHeader
        value={searchQuery}
        onChangeText={handleSearch}
        placeholder={searchPlaceholder}
        cancelRoute={cancelRoute}
        maxLength={maxLength}
      />
      <ScrollView 
        style={styles.content}
        contentContainerStyle={styles.contentContainer}
        showsVerticalScrollIndicator={false}
      >
        {renderContent()}
      </ScrollView>
    </View>
  );
};

export default React.memo(SearchPage);
